import styles from "./FeedBackPage.module.css";
import React, {useRef, useState} from "react";
import {Administrator} from "../../../services/models/administrator";
import {SubUser} from "../../../services/models/sub_user";
import stylesInputs from "../../../assets/styles/inputs.module.css";
import {useForm} from "react-hook-form";
import {PageHeader} from "../../../components/shared/PageHeader/PageHeader";
import {FormActionButton} from "../../../components/shared/buttons/FormActionButton";
import {ALLOWED_FILE_TYPES, MAX_FILE_SIZE} from '../../../Utils/uploadFileSettings';
import {useCreateFeedBack} from '../../../hooks/useQuery/useCreateFeedBack';

interface FeedBackPageProps {
    user: Administrator | SubUser | null;
}

interface FormData {
    about: string;
    text: string;
    contact: string;
    files?: File[]
}


export const FeedBackPage: React.FC<FeedBackPageProps> = ({user}) => {
    const {
        register,
        handleSubmit,
        formState: {errors},
        reset,
        setValue,
        watch,
        trigger,
        getValues
    } = useForm<FormData>({
        mode: "onChange",
        defaultValues: {
            about: "",
            text: "",
            contact: "",
        }
    });

    // Интеграция с отправкой на сервер
    const createFeedBack = useCreateFeedBack();
    // После успешной отправки очищаем форму и выбранные файлы
    React.useEffect(() => {
        if (createFeedBack.isSuccess) {
            reset();
            setSelectedFiles([]);
        }
    }, [createFeedBack.isSuccess, reset]);

    const handleSend = async () => {
        const valid = await trigger(["about", "text", "contact"]);
        if (!valid) return;
        const values = getValues();
        createFeedBack.mutate({
            data: {
                about: values.about,
                contact: values.contact,
                text: values.text,
                files: selectedFiles.length > 0 ? selectedFiles : undefined
            }
        });
    }

    // --- Локальное состояние для файлов ---
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [filesError, setFilesError] = useState<string | null>(null);
    const filesInputRef = useRef<HTMLInputElement>(null);

    // Формируем список только image/* типов из ALLOWED_FILE_TYPES.FILES
    const allowedImageTypes = ALLOWED_FILE_TYPES.FILES.filter(type => type.startsWith('image/'));
    const acceptString = allowedImageTypes.join(',');

    // Валидация файлов (до 4, только картинки, размер)
    const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;
        const arr = Array.from(files);
        if (arr.length > 4) {
            setFilesError('Можно выбрать максимум 4 файла');
            setSelectedFiles([]);
            setValue('files', undefined);
            return;
        }
        for (const file of arr) {
            if (!allowedImageTypes.includes(file.type)) {
                setFilesError('Можно загружать только изображения допустимых форматов');
                setSelectedFiles([]);
                setValue('files', undefined);
                return;
            }
            if (file.size > MAX_FILE_SIZE) {
                setFilesError(`Размер файла не должен превышать ${Math.round(MAX_FILE_SIZE / 1024 / 1024)} МБ`);
                setSelectedFiles([]);
                setValue('files', undefined);
                return;
            }
        }
        setFilesError(null);
        setSelectedFiles(arr);
        setValue('files', arr);
    };

    return (
        <>
            <PageHeader
                buttons={<></>}
                isAdmin={false}
                title={"Форма обратной связи"}
            />

            <div className={styles.reportPage}>
                <form className={styles.form}>
                    <div className={`mb-3 ${stylesInputs.formGroup}`}>
                        <label className={stylesInputs.label}>
                            Тема обращения
                        </label>
                        <div className={stylesInputs.inputWrapper}>
                            <input
                                type="text"
                                className={stylesInputs.mainInput}
                                placeholder={"Тема"}
                                {...register("about", {required: true})}
                            />
                        </div>
                        {errors.about && (
                            <span className={stylesInputs.error}>Поле обязательно для заполнения</span>
                        )}
                    </div>
                    <div className={`mb-3 ${stylesInputs.formGroup}`}>
                        <label className={stylesInputs.label}>
                            Текст обращения
                        </label>
                        <div className={stylesInputs.inputWrapper}>
                            <textarea
                                aria-rowspan={10}
                                className={`${stylesInputs.mainInput} ${styles.textarea}`}
                                placeholder={" Текст обращения"}
                                {...register("text", {required: true})}
                            />
                        </div>
                        {errors.text && (
                            <span className={stylesInputs.error}>Поле обязательно для заполнения</span>
                        )}
                    </div>
                    <div className={`mb-3 ${stylesInputs.formGroup}`}>
                        <label className={stylesInputs.label}>
                            Контакты для связи
                        </label>
                        <div className={stylesInputs.inputWrapper}>
                            <input
                                type="text"
                                className={stylesInputs.mainInput}
                                placeholder={"Номер телефона или email"}
                                {...register("contact", {required: true})}
                            />
                        </div>
                        {errors.contact && (
                            <span className={stylesInputs.error}>Поле обязательно для заполнения</span>
                        )}
                    </div>
                    {/* Инпут для загрузки до 4 изображений */}
                    <div className={`mb-3 ${stylesInputs.formGroup}`}>
                        <label className={stylesInputs.label}>Прикрепить изображения (до 4 файлов,
                            jpg/png/gif/webp)</label>
                        <div className={stylesInputs.fileUpload +
                            (selectedFiles.length > 0 ? ' ' + stylesInputs.fileUploadWrapperHasFiles : '')}
                             onClick={() => filesInputRef.current?.click()}
                             style={{cursor: 'pointer'}}
                        >
                            <input
                                type="file"
                                accept={acceptString}
                                multiple
                                className={stylesInputs.fileUploadInput}
                                onChange={handleFilesChange}
                                ref={filesInputRef}
                                style={{display: 'none'}}
                            />
                            <div
                                className={
                                    stylesInputs.fileUploadContent +
                                    (selectedFiles.length > 0 ? ' ' + stylesInputs.fileUploadHasFiles : '')
                                }

                            >
                                {selectedFiles.length === 0 ? (
                                    <span>Выбрать файлы</span>
                                ) : (
                                    <div className={stylesInputs.selectedFilesList}>
                                        {selectedFiles.map((file, idx) => (
                                            <div key={idx} className={stylesInputs.selectedFileItem}>
                                                <img
                                                    src={URL.createObjectURL(file)}
                                                    alt={file.name}
                                                    style={{
                                                        width: 40,
                                                        height: 40,
                                                        objectFit: 'cover',
                                                        borderRadius: 4,
                                                        marginRight: 8
                                                    }}
                                                />
                                                <span style={{fontSize: 12}}>{file.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        {filesError && (
                            <span className={stylesInputs.error}>{filesError}</span>
                        )}
                    </div>
                    <FormActionButton text={"Отправить"}
                                      type={"button"}
                                      onClick={handleSend}
                                      disabled={createFeedBack.isPending}
                    />
                </form>
            </div>
        </>

    );

};
